/**
 * Server-side module for Server Function Bridge
 * Sets up WebSocket server to handle client function calls
 */

const WebSocket = require('ws');
const fs = require('fs');
const path = require('path');
const https = require('https');
const http = require('http');
require('dotenv').config();

const DEFAULT_OPTIONS = {
  port: process.env.WS_PORT || 8080,
  scriptsDir: process.env.SCRIPTS_DIR || path.join(process.cwd(), 'src', 'scripts'),
  path: '/ws',
  logging: {
    enabled: true,
    detailed: false
  },
  createSample: true
};

/**
 * Setup the Server Function Bridge with an existing Express app
 * @param {Object} app - Express application instance
 * @param {Object} options - Configuration options
 * @returns {Object} - WebSocket server and HTTP/HTTPS server instances
 */
function setupServerFunctionBridge(app, options = {}) {
  // Merge default options with provided options
  const config = { ...DEFAULT_OPTIONS, ...options };
  
  // Initialize logging
  const logging = typeof config.logging === 'boolean' 
    ? { enabled: config.logging, detailed: false }
    : { ...{ enabled: true, detailed: false }, ...config.logging };
  
  // Initialize WebSocket server
  let wss;
  let server;
  
  // Log function
  function log(message, details = null) {
    if (!logging.enabled) return;
    
    const timestamp = new Date().toISOString();
    console.log(`[${timestamp}] [SERVER-FUNCTION-BRIDGE] ${message}`);
    
    if (details && logging.detailed) {
      console.log(typeof details === 'object' ? JSON.stringify(details, null, 2) : details);
    }
  }
  
  log(`Initializing Server Function Bridge with options:`, config);
  
  // Ensure scripts directory exists
  if (!fs.existsSync(config.scriptsDir)) {
    log(`Creating scripts directory: ${config.scriptsDir}`);
    try {
      fs.mkdirSync(config.scriptsDir, { recursive: true });
    } catch (err) {
      log(`Failed to create scripts directory:`, err);
    }
  }
  
  // Create WebSocket server
  if (config.ssl) {
    log('Creating HTTPS server with SSL');
    
    server = https.createServer(config.ssl);
    
    wss = new WebSocket.Server({
      server,
      path: config.path
    });
    
    server.listen(config.port, () => {
      log(`HTTPS server for WebSockets listening on port ${config.port}`);
    });
  } else if (app) {
    log('Using existing Express app');
    
    // Use the app's underlying HTTP server
    if (!app.listening) {
      log('Express app is not listening yet. WebSocket server will start when Express app starts listening.');
    }
    
    // Get the server when the app starts listening
    const originalListen = app.listen;
    app.listen = function(...args) {
      const expressServer = originalListen.apply(app, args);
      
      wss = new WebSocket.Server({
        server: expressServer,
        path: config.path
      });
      
      setupWebSocketHandlers(wss, config, log);
      
      log(`WebSocket server attached to Express app`);
      return expressServer;
    };
  } else {
    log('Creating standalone HTTP server');
    
    server = http.createServer();
    
    wss = new WebSocket.Server({
      server,
      path: config.path
    });
    
    server.listen(config.port, () => {
      log(`HTTP server for WebSockets listening on port ${config.port}`);
    });
  }
  
  // Setup WebSocket handlers if we already have a server
  if (wss) {
    setupWebSocketHandlers(wss, config, log);
  }
  
  // Create sample method if enabled
  if (config.createSample) {
    createSampleMethod(config.scriptsDir, log);
  }
  
  return { wss, server };
}

/**
 * Setup WebSocket handlers
 * @private
 * @param {WebSocket.Server} wss - WebSocket server instance
 * @param {Object} config - Configuration options
 * @param {Function} log - Logging function
 */
function setupWebSocketHandlers(wss, config, log) {
  // Track connection stats
  const wsStats = {
    totalConnections: 0,
    activeConnections: 0,
    messageCount: 0,
    errorCount: 0
  };
  
  log('Setting up WebSocket handlers');
  
  // Log when the WebSocket server starts listening
  wss.on('listening', () => {
    log(`WebSocket server is now listening on path ${config.path}`);
    log(`Looking for method files in: ${config.scriptsDir}`);
    
    // List available methods at startup
    if (fs.existsSync(config.scriptsDir)) {
      const files = fs.readdirSync(config.scriptsDir);
      log(`Available methods:`, files);
    }
  });
  
  // Log any server-level errors
  wss.on('error', (error) => {
    wsStats.errorCount++;
    log('WebSocket server error:', error);
  });
  
  // Handle WebSocket connections
  wss.on('connection', (ws, req) => {
    wsStats.totalConnections++;
    wsStats.activeConnections++;
    
    const clientIp = req.socket.remoteAddress;
    const connectionId = `wss-${Date.now()}-${wsStats.totalConnections}`;
    
    log('New WebSocket connection established', {
      connectionId,
      clientIp,
      path: req.url
    });
    
    // Authenticate connection if auth function provided
    if (typeof config.authFunction === 'function') {
      try {
        if (!config.authFunction(req)) {
          log('Connection authentication failed', { connectionId, clientIp });
          ws.close(4001, 'Authentication failed');
          return;
        }
      } catch (error) {
        log('Error in authentication function', { connectionId, error });
        ws.close(4002, 'Authentication error');
        return;
      }
    }
    
    // Log when the connection closes
    ws.on('close', (code, reason) => {
      wsStats.activeConnections--;
      log('WebSocket connection closed', {
        connectionId,
        code,
        reason: reason.toString ? reason.toString() : reason
      });
    });
    
    // Log errors on this connection
    ws.on('error', (error) => {
      wsStats.errorCount++;
      log('WebSocket connection error', {
        connectionId,
        error
      });
    });
    
    // Handle WebSocket messages
    ws.on('message', async (message) => {
      wsStats.messageCount++;
      let requestId = 'unknown';
      
      try {
        // Convert Buffer to string if needed
        const messageStr = message.toString();
        
        log('WebSocket message received', {
          connectionId,
          messageLength: messageStr.length
        });
        
        const request = JSON.parse(messageStr);
        requestId = request.requestId || 'unknown';
        const { methodName, args = [] } = request;
        
        log(`WebSocket request details:`, {
          connectionId,
          requestId,
          methodName
        });
        
        // Look for the server-side method in the scripts directory
        const methodPath = path.join(config.scriptsDir, `${methodName}.js`);
        
        log(`Looking for method file:`, {
          connectionId,
          requestId, 
          methodPath
        });
        
        if (!fs.existsSync(methodPath)) {
          const error = `Method file not found: ${methodName}.js`;
          log(`Error: ${error}`, {
            connectionId,
            requestId
          });
          
          ws.send(JSON.stringify({
            requestId,
            error
          }));
          return;
        }
        
        // Clear require cache to ensure we get the latest version
        delete require.cache[require.resolve(methodPath)];
        
        // Load the method
        try {
          log(`Loading method from file: ${methodPath}`, {
            connectionId,
            requestId
          });
          const method = require(methodPath);
          
          if (typeof method !== 'function') {
            const error = `Invalid method in ${methodName}.js: expected a function`;
            log(`Error: ${error}`, {
              connectionId,
              requestId
            });
            
            ws.send(JSON.stringify({
              requestId,
              error
            }));
            return;
          }
          
          // Execute the method
          try {
            log(`Executing method: ${methodName}`, {
              connectionId,
              requestId
            });
            
            const result = await method(...args);
            
            log(`Method executed successfully`, {
              connectionId,
              requestId
            });
            
            // Send back the result
            ws.send(JSON.stringify({
              requestId,
              result
            }));
            
            log(`Response sent back to client`, {
              connectionId,
              requestId
            });
          } catch (error) {
            log(`Error executing ${methodName}:`, {
              connectionId,
              requestId,
              error
            });
            
            ws.send(JSON.stringify({
              requestId,
              error: error.message || 'Error executing server method'
            }));
          }
        } catch (error) {
          log(`Error requiring module ${methodPath}:`, {
            connectionId,
            requestId,
            error
          });
          
          ws.send(JSON.stringify({
            requestId,
            error: `Error loading method: ${error.message}`
          }));
        }
      } catch (error) {
        log('WebSocket message parsing error:', {
          connectionId,
          error
        });
        
        ws.send(JSON.stringify({
          requestId,
          error: 'Invalid message format'
        }));
      }
    });
    
    // Send a welcome message to confirm connection
    try {
      ws.send(JSON.stringify({
        type: 'system',
        message: 'WebSocket connection established successfully',
        connectionId,
        timestamp: new Date().toISOString()
      }));
      
      log('Welcome message sent to client', {
        connectionId
      });
    } catch (error) {
      log('Error sending welcome message', {
        connectionId,
        error
      });
    }
  });
  
  // Log periodic WebSocket stats
  if (config.logging.detailed) {
    setInterval(() => {
      if (wsStats.totalConnections > 0) {
        log('WebSocket server stats', wsStats);
      }
    }, 60000); // Log every minute
  }
}

/**
 * Create a sample "Hello World" method file
 * @private
 * @param {string} scriptsDir - Directory to create the sample in
 * @param {Function} log - Logging function
 */
function createSampleMethod(scriptsDir, log) {
  const sampleMethodPath = path.join(scriptsDir, 'helloWorld.js');
  
  if (!fs.existsSync(sampleMethodPath)) {
    log(`Creating sample helloWorld.js method at: ${sampleMethodPath}`);
    
    try {
      const sampleMethod = `/**
 * Hello World server function example
 * 
 * This is a sample server method that can be called via WebSocket
 * from client-side JavaScript using the Server Function Bridge.
 * 
 * @param {string} name - Name to say hello to
 * @returns {Object} - Response object with greeting
 */
module.exports = async function helloWorld(name = 'World') {
  // This code runs on the server
  return {
    message: \`Hello, \${name}!\`,
    timestamp: new Date().toISOString(),
    serverInfo: {
      platform: process.platform,
      nodeVersion: process.version
    }
  };
};
`;
      
      fs.writeFileSync(sampleMethodPath, sampleMethod);
      log('Sample method created successfully');
    } catch (error) {
      log('Error creating sample method:', error);
    }
  }
}

// Export the main function
module.exports = {
  setupServerFunctionBridge
};